import React, { ChangeEvent, useEffect, useRef, useState } from 'react'
import styled from '@emotion/styled'
import { SketchPicker, ColorResult } from 'react-color'
import { Popper } from '@mui/material'
import { Levels, Shapes } from 'src/components/theme'
import { MouseLeaveListener } from 'src/components/mouse-leave-listener'
import { Flex } from 'src/components/flex-containers'
import { useRecoilState } from 'recoil'
import { userSavedColors } from 'src/atoms/colors'
import { SKETCH_PICKER_DEFAULT_PRESET_COLORS } from 'src/components/theme/colors'
import { TextField as UnstyledTextField, TextFieldProps } from 'src/components/text-field'

export type ColorInputProps = Omit<TextFieldProps, 'onChange'> & {
  value?: string
  onChange: (value: string) => void
  onClose?: () => void
}

const PickerWrapper = styled.div`
  position: relative;
  div[role='tooltip'] {
    z-index: ${Levels.popper};
  }
`

const PopperWrapper = styled(Flex)`
  flex-direction: column;
  justify-content: center;
  gap: ${Shapes.spacing[2]}px;
  margin: ${Shapes.spacing[2]}px;
  align-items: center;
`

const ColorBox = styled.div<{ color: string }>`
  position: absolute;
  margin: ${Shapes.spacing[3]}px;
  width: 18px;
  height: 18px;
  background-color: ${({ color }) => color};
  border-radius: ${Shapes.smallBorderRadius}px;
  z-index: 1;
`

const TextField = styled(UnstyledTextField)`
  & .MuiInputBase-input {
    padding-left: 38px !important;
  }
`

const MAXIMUM_CUSTOM_COLORS = 7

export const ColorPickerInput = (props: ColorInputProps) => {
  const { value: valueFromProps = '#FFFFFF', onChange, onClose, ...textFieldProps } = props

  const [customColors, setCustomColors] = useRecoilState(userSavedColors)
  const [maybeSave, setMaybeSave] = useState<string | undefined>(undefined)
  const [value, setValue] = useState<string>(valueFromProps)
  const [open, setOpen] = useState<boolean>(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const updateCustomColors = () => {
    if (!maybeSave) {
      return
    }
    const temp = [...customColors]
    const totalSaved = temp.unshift(maybeSave)
    if (totalSaved > MAXIMUM_CUSTOM_COLORS) {
      temp.pop()
    }
    setCustomColors(temp)
  }

  const presetColors = () => {
    return [...customColors, ...SKETCH_PICKER_DEFAULT_PRESET_COLORS.slice(customColors.length)]
  }

  const closePicker = () => {
    updateCustomColors()
    setOpen(false)
    if (open && onClose) {
      onClose()
    }
    setMaybeSave(undefined)
  }

  useEffect(() => {
    return () => {
      closePicker()
    }
  }, [])

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.value = value || ''
    }
  }, [value, inputRef])

  useEffect(() => {
    setValue(valueFromProps)
  }, [valueFromProps])

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const hexString = e.target.value.substring(1)
    if (hexString.length !== 0 && isNaN(Number('0x' + hexString))) {
      e.target.value = valueFromProps || '#FFFFFF'
    }
    if (e.target.value.length > 9) {
      e.target.value = e.target.value.substring(0, 9)
    }
    if (e.target.value.charAt(0) !== '#') {
      e.target.value = `#${e.target.value}`
    }
    onChange(e.target.value)
    setValue(e.target.value)
  }

  const handleColorChange = (e: ColorResult) => {
    const chosenColor = e.hex.toUpperCase()
    onChange(chosenColor)
    if (presetColors().includes(chosenColor)) {
      setMaybeSave(undefined)
    } else {
      setMaybeSave(chosenColor)
    }
  }

  return (
    <MouseLeaveListener onLeave={closePicker}>
      <PickerWrapper>
        <ColorBox color={value} />
        <TextField
          inputRef={inputRef}
          style={{ width: '100%' }}
          {...textFieldProps}
          onFocus={() => setOpen(true)}
          onChange={handleInputChange}
        />
        <Popper open={open} anchorEl={inputRef.current} placement="bottom" style={{ zIndex: Levels.popper }}>
          <PopperWrapper center col>
            <SketchPicker color={value} onChange={handleColorChange} presetColors={presetColors()} />
          </PopperWrapper>
        </Popper>
      </PickerWrapper>
    </MouseLeaveListener>
  )
}
